import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const FREE_ITEM_PRODUCT_ID = 9023281955056;

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #refreshVersion = 0;
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('inveterate-atced', this.#handleMembershipAdd);
    document.addEventListener('cart:refresh', this.#handleExternalRefresh);

    // Handle upsell "Add" buttons inside the drawer (event delegation)
    this.addEventListener('click', this.#handleUpsellClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('inveterate-atced', this.#handleMembershipAdd);
    document.removeEventListener('cart:refresh', this.#handleExternalRefresh);
    this.#refreshVersion += 1;
    this.removeEventListener('click', this.#handleUpsellClick);
  }

  #handleCartAdd = (event) => {
    const data = event.detail?.data;
    if (data?.didError) return;

    // A native theme update supersedes any older external refresh.
    if (data?.source !== 'cart-drawer-sync') this.#refreshVersion += 1;
    if (this.hasAttribute('auto-open') && data?.openDrawer !== false) {
      this.showDialog();
    }

    // Section morphing runs in the other cart:update listeners first.
    queueMicrotask(() => this.#updateCheckoutState(event.detail?.resource));
  };

  #handleMembershipAdd = () => {
    this.#refreshCart({ openDrawer: true }).catch(this.#handleRefreshError);
  };

  #handleExternalRefresh = (event) => {
    this.#refreshCart({ openDrawer: event.detail?.openDrawer === true }).catch(this.#handleRefreshError);
  };

  #handleRefreshError = (error) => {
    console.error('[CartDrawer] Failed to refresh cart:', error);
    const status = this.querySelector('[data-minicart-status]');
    if (status) status.textContent = 'Your cart could not be refreshed. Please refresh the page to see your items.';
  };

  /** Refresh external additions through the same event contract as native product forms. */
  #refreshCart = async ({ openDrawer = false } = {}) => {
    const version = ++this.#refreshVersion;
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = [...new Set(
      Array.from(document.querySelectorAll('cart-items-component'))
        .map((element) => element.dataset.sectionId)
        .filter(Boolean)
    )];
    if (!sectionIds.length) throw new Error('Cart section missing');

    // Render in storefront context: the cart page intentionally omits the drawer.
    // Preserve preview parameters when refreshing from an app-proxy page.
    const sectionUrl = new URL(window.location.href);
    sectionUrl.pathname = root;
    sectionUrl.searchParams.delete('section_id');
    sectionUrl.searchParams.set('sections', sectionIds.join(','));
    const options = { headers: { Accept: 'application/json' }, cache: 'no-store' };
    const [cartResponse, sectionResponse] = await Promise.all([
      fetch(`${root}cart.js`, options),
      // /cart negotiates on Accept: application/json returns cart data, not sections.
      fetch(sectionUrl.toString(), { cache: 'no-store' }),
    ]);
    if (!cartResponse.ok || !sectionResponse.ok) throw new Error('Unable to load updated cart');
    const [cart, sections] = await Promise.all([cartResponse.json(), sectionResponse.json()]);
    if (version !== this.#refreshVersion || !this.isConnected) return;
    if (!Number.isFinite(cart.item_count) || !Array.isArray(cart.items)) {
      throw new Error('Invalid cart response');
    }
    if (sectionIds.some((id) => typeof sections?.[id] !== 'string' || !sections[id].trim())) {
      throw new Error('Cart section rendering failed');
    }

    // CartItemsComponent morphs the sections; CartIcon uses this absolute item count.
    document.dispatchEvent(new CartAddEvent(cart, this.id, {
      source: 'cart-drawer-sync',
      itemCount: cart.item_count,
      sections,
      openDrawer,
    }));
  };

  open() {
    this.showDialog();

    // Evaluate checkout eligibility every time the drawer opens
    this.#updateCheckoutState();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Click handler for upsell add buttons.
   */
  #handleUpsellClick = async (event) => {
    const btn = event.target?.closest?.('.miniCartUpsellItemBtn');
    if (!btn || btn.disabled) return;

    event.preventDefault();

    const variantIdRaw = btn.dataset.variantId;
    const variantId = Number.parseInt(variantIdRaw, 10);

    if (!variantId || Number.isNaN(variantId)) return;

    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    const status = this.querySelector('[data-minicart-status]');

    let added = false;
    try {
      // Read Shoplift's current assignment from this product's card at click time.
      const shopliftInput = btn.closest('.miniCartUpsellItem')
        ?.querySelector('input[name="properties[_slpt]"]');
      await this.#addVariantToCart(variantId, 1, shopliftInput?.value);
      added = true;
      await this.#refreshCart();
      if (status) status.textContent = 'Item added to cart.';
    } catch (err) {
      console.error('[CartDrawer] Failed to add or refresh upsell item:', err);
      if (status) status.textContent = added
        ? 'Item added, but your cart could not be refreshed. Please refresh the page.'
        : 'The item could not be added. Please try again.';
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  };

  #addVariantToCart = async (variantId, quantity = 1, shopliftAssignment) => {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    if (shopliftAssignment) {
      formData.append('properties[_slpt]', shopliftAssignment);
    }


    const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData,
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'Add to cart failed');
    return data;
  };

  /**
   * Fetches the current cart and disables the checkout button if the only
   * item(s) in the cart are the free item (FREE_ITEM_PRODUCT_ID).
   */
  #updateCheckoutState = async (cart) => {
    try {
      if (!Array.isArray(cart?.items)) {
        const res = await fetch(`${Shopify.routes.root}cart.js`);
        if (!res.ok) throw new Error('Unable to load checkout eligibility');
        cart = await res.json();
      }

      const items = cart.items ?? [];
      const onlyFreeItem =
        items.length > 0 &&
        items.every((item) => item.product_id === FREE_ITEM_PRODUCT_ID);

      const checkoutBtns = this.querySelectorAll('[name="checkout"], .cart__checkout-button, a[href="/checkout"]');

      // Remove any existing warning message before re-evaluating
      this.querySelector('#free-item-checkout-warning')?.remove();

      if (onlyFreeItem) {
        checkoutBtns.forEach((btn) => {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        });

        // Insert warning message after the first checkout button found
        const firstBtn = checkoutBtns[0];
        if (firstBtn) {
          const warning = document.createElement('p');
          warning.id = 'free-item-checkout-warning';
          warning.style.cssText = 'margin-top: 0.5rem; color: red; font-size: 0.875rem; text-align: center;';
          warning.textContent = 'You must have at least 1 paid item to checkout';
          firstBtn.insertAdjacentElement('afterend', warning);
        }
      } else {
        checkoutBtns.forEach((btn) => {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
        });
      }
    } catch (err) {
      console.error('[CartDrawer] Failed to evaluate checkout state:', err);
    }
  };


}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
